import { FC, memo, ReactNode } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { T_KEYS } from '../../constants';

interface IConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description?: string | ReactNode;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: 'primary' | 'danger';
  isLoading?: boolean;
  showSupportLink?: boolean;
  isRtl?: boolean;
}

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${(props) => (props.$isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContainer = styled.div<{ $isRtl?: boolean }>`
  background: white;
  border-radius: 24px;
  padding: 32px;
  max-width: 480px;
  width: 90%;
  box-shadow: 0px 20px 40px rgba(16, 24, 40, 0.15);
  animation: slideUp 0.3s ease;
  direction: ${(props) => (props.$isRtl ? 'rtl' : 'ltr')};
  text-align: ${(props) => (props.$isRtl ? 'right' : 'left')};

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const Title = styled.h2`
  font-weight: 700;
  font-size: 20px;
  color: #130080;
  margin: 0 0 16px;
`;

const Description = styled.div`
  font-size: 14px;
  color: #697586;
  line-height: 1.6;
  margin-bottom: 24px;

  p {
    margin: 0 0 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const ButtonsRow = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button<{ $variant?: 'primary' | 'danger' | 'secondary'; $isLoading?: boolean }>`
  flex: 1;
  padding: 14px 24px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 14px;
  cursor: ${(props) => (props.$isLoading ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  opacity: ${(props) => (props.$isLoading ? 0.7 : 1)};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  ${(props) => {
    switch (props.$variant) {
      case 'danger':
        return `
          background: #F04438;
          color: white;
          border: none;
          
          &:hover {
            background: #D92D20;
          }
        `;
      case 'secondary':
        return `
          background: white;
          color: #344054;
          border: 1px solid #D0D5DD;
          
          &:hover {
            background: #F9FAFB;
          }
        `;
      default:
        return `
          background: #5B3FFF;
          color: white;
          border: none;
          
          &:hover {
            background: #4A2FE6;
          }
        `;
    }
  }}
`;

const SupportLink = styled.a`
  display: block;
  text-align: center;
  margin-top: 16px;
  font-size: 14px;
  color: #5B3FFF;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ConfirmationModal: FC<IConfirmationModalProps> = memo(({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmText,
  cancelText,
  confirmVariant = 'primary',
  isLoading = false,
  showSupportLink = false,
  isRtl = false,
}) => {
  const { t } = useTranslation();

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  return (
    <Overlay $isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContainer $isRtl={isRtl}>
        <Title>{title}</Title>
        {description && (
          <Description>
            {typeof description === 'string' ? <p>{description}</p> : description}
          </Description>
        )}
        <ButtonsRow>
          <Button
            $variant={confirmVariant}
            onClick={onConfirm}
            disabled={isLoading}
            $isLoading={isLoading}
          >
            {isLoading && <Spinner />}
            {confirmText || t(T_KEYS.COMMON_CONFIRM)}
          </Button>
          <Button
            $variant="secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            {cancelText || t(T_KEYS.COMMON_CANCEL)}
          </Button>
        </ButtonsRow>
        {showSupportLink && (
          <SupportLink href="#" onClick={(e) => e.preventDefault()}>
            {t(T_KEYS.CONTACT_SUPPORT)}
          </SupportLink>
        )}
      </ModalContainer>
    </Overlay>
  );
});

ConfirmationModal.displayName = 'ConfirmationModal';

export default ConfirmationModal;
